import { DatePipe, SlicePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppInitAuthManagerService } from '@meddev/fe-shared';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { DataTableColumnCellDirective, DataTableColumnDirective, DatatableComponent, PageEvent, SelectionType } from '@swimlane/ngx-datatable';
import { fromEvent, iif, of, Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { BaseState } from 'src/app/_common/state/base/base.state';
import { MenuStateActions } from 'src/app/_common/state/menu/menu.actions';
import dayjs from '../../_common/date/dayjs';
import { WindowResizeService } from '../../_common/services/window-resize.service';
import { ResReservationDetailsComponent } from '../reservations/res-reservation-details/res-reservation-details.component';
import { NotificationEnum } from './../../_common/enums/notification.enum';
import { NotificationElement } from './../../_common/model/notificationElement';
import { NavigateService } from './../../_common/services/navigate.service';
import { NotificationsService } from './../../_common/services/notifications.service';

@Component({
    // @TODO: Ignored with eslint-interactive on 2023-09-15
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'index',
    templateUrl: './index.component.html',
    styleUrls: ['./index.component.scss'],
    imports: [DatatableComponent, DataTableColumnDirective, DataTableColumnCellDirective, NgbTooltip, SlicePipe, DatePipe],
})
export class IndexComponent implements OnInit {
    private store = inject(Store);
    private router = inject(Router);
    private notifRest = inject(NotificationsService);
    private navigate = inject(NavigateService);
    private appInitAuthManagerService = inject(AppInitAuthManagerService);
    private windowResize = inject(WindowResizeService);

    protected readonly SelectionType = SelectionType;
    private bsId = this.store.selectSnapshot(BaseState.getBSId);
    public allNotifications: NotificationElement[] = [];
    private onDestroy$ = new Subject<void>();

    public paging = {
        count: 0,
        page: 0,
        pageSize: 12,
    };

    private readonly allowedTypes: NotificationEnum[] = [
        NotificationEnum.RESERVATION_CANCELED,
        NotificationEnum.CASE_CANCELED,
        NotificationEnum.ASISTANT_CASE_CONTRACTOR_COMMENT_ADDED,
        NotificationEnum.ASISTANT_CASE_CONTRACTOR_DOCUMENT_ADDED,
    ];

    ngOnInit() {
        this.store.dispatch(
            new MenuStateActions.SetHeaderData({
                iconClass: 'las la-calendar-check',
                title: 'preventivni.si',
                description: ' ',
            }),
        );

        this.updatePageSizeFromViewport();
        this.getNotifications();

        fromEvent(window, 'resize')
            .pipe(auditTime(150), takeUntil(this.onDestroy$))
            .subscribe(() => {
                const before = this.paging.pageSize;
                this.updatePageSizeFromViewport();

                if (this.paging.pageSize !== before) {
                    this.paging.page = 0;
                    this.getNotifications();
                }
            });
    }

    private updatePageSizeFromViewport() {
        if (window.innerWidth > 0) {
            // keep a safe minimum to avoid invalid requests / table state
            this.paging.pageSize = Math.max(1, Math.floor((window.innerHeight - 230) / 50));
        }
    }

    public getNotifications() {
        this.notifRest
            .getNotifications(this.bsId, {
                from: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
                to: dayjs().add(1, 'day').format('YYYY-MM-DD'),
                page: this.paging.page,
                pageSize: this.paging.pageSize,
                types: this.allowedTypes,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(({ result, count, page, pageSize }) => {
                this.allNotifications = this.notifRest.prepareNotifications(result);
                this.paging.count = count ?? 0;
                this.paging.page = page ?? 0;
                // Keep pageSize controlled by viewport/table config.
                // Some table events/reporting can reflect "rows on this page" rather than the configured limit.
                // Backend also clamps pageSize, so we avoid echoing it back into the UI state.
                void pageSize;
            });
    }

    public onPage(event: PageEvent) {
        this.paging.page = event.offset;
        this.getNotifications();
    }

    public notificationSelected({ selected }: { selected: NotificationElement[] }) {
        iif(() => !selected[0].seen, this.notifRest.markNotificationAsSeen(this.bsId, selected[0]?.id), of(true))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                selected[0].seen = true;
                this.navigate.openSidePanel({
                    component: ResReservationDetailsComponent,
                    params: {
                        setData: { reservationDetails: { caseId: selected[0]?.data?.caseId }, selectedTab: selected[0]?.tab },
                    },
                    title: '',
                });
            });
    }

    logout() {
        this.appInitAuthManagerService.performLogout();
    }

    // @TODO: Ignored with eslint-interactive on 2023-09-15
    // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.navigate.closeSidePanel();
    }

    getRowClass(row: NotificationElement): string {
        if (row.seen) return 'seen';
        return 'not-seen';
    }
}
